import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useLocation, Link } from 'react-router-dom';
import { CheckCircle2, Loader2, MessageCircle, Phone, Camera, Calendar as CalIcon, Truck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const useQuoteId = () => {
  const search = useLocation().search;
  return new URLSearchParams(search).get('quote');
};

export default function Booked() {
  const quoteId = useQuoteId();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quote, setQuote] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!quoteId) { setError('missing'); setLoading(false); return; }
      try {
        const { data } = await axios.get(`${API}/estimate/${quoteId}`);
        if (!cancelled) {
          setQuote(data);
          try { localStorage.removeItem('mv_pending_quote'); } catch (_) {}
        }
      } catch (e) {
        if (!cancelled) setError('notfound');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    run();
    return () => { cancelled = true; };
  }, [quoteId]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="flex items-center gap-3 text-slate-600">
          <Loader2 className="w-5 h-5 animate-spin text-[color:var(--mv-orange)]" />
          Loading your booking…
        </div>
      </div>
    );
  }

  if (error || !quote) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-6">
        <div className="max-w-md w-full text-center bg-white rounded-xl border border-slate-200 shadow-sm p-8">
          <h1 className="font-display text-2xl font-extrabold text-[color:var(--mv-navy)]">
            We couldn't find your booking
          </h1>
          <p className="text-sm text-slate-600 mt-2">
            Please WhatsApp us with your quote reference and we'll sort it right away.
          </p>
          <a href="https://wa.me/447985291097" target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebc5a] text-white font-bold px-4 py-2 rounded-md">
            <MessageCircle className="w-4 h-4" /> WhatsApp us
          </a>
          <div className="mt-3">
            <Link to="/" className="text-sm text-[color:var(--mv-navy)] font-semibold hover:text-[color:var(--mv-orange)]">← Back to home</Link>
          </div>
        </div>
      </div>
    );
  }

  const booking = quote.booking || {};
  const ref = (quoteId || '').slice(0, 6).toUpperCase();
  const sameDay = !!booking.same_day;
  const finalPrice = booking.final_price || quote.price || 0;
  const deposit = quote.deposit || 0;
  const balance = Math.max(finalPrice - deposit, 0);

  const waText = encodeURIComponent(
    `Hi Move Van Ltd — booking ref #${ref} (${quote.name || ''}). Date: ${booking.date || ''} at ${booking.time || ''}. Final total £${finalPrice}. Can you confirm please?`
  );
  const waUrl = `https://wa.me/447985291097?text=${waText}`;

  return (
    <div className="min-h-[60vh] bg-[color:var(--mv-sky)] py-10 px-4">
      <div className="max-w-xl mx-auto">
        <div className="rounded-2xl bg-white shadow-sm border border-slate-200 overflow-hidden">
          <div className="bg-gradient-to-br from-[color:var(--mv-navy)] to-[#13315c] text-white p-6">
            <div className="inline-flex items-center gap-2 bg-emerald-500/20 border border-emerald-400/40 text-emerald-100 px-3 py-1 rounded-full text-[11px] font-bold tracking-wider uppercase">
              <CheckCircle2 className="w-3.5 h-3.5" /> You're booked in
            </div>
            <div className="font-display text-3xl font-extrabold mt-3" data-testid="booked-headline">
              Thanks{quote.name ? `, ${quote.name}` : ''} — your slot is locked.
            </div>
            <div className="text-sm text-white/85 mt-2 leading-relaxed">
              A confirmation email is on its way to <strong className="text-white">{quote.email || 'your inbox'}</strong>. Please <strong>screenshot this page</strong> and keep your booking reference safe — just in case.
            </div>
          </div>

          <div className="p-6">
            {/* Reference */}
            <div className="rounded-xl border-2 border-dashed border-[color:var(--mv-orange)] bg-orange-50 p-4 flex items-center justify-between gap-3" data-testid="booking-ref-card">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] font-black text-[color:var(--mv-orange)]">Booking reference</div>
                <div className="font-display text-3xl font-extrabold text-[color:var(--mv-navy)] mt-1 leading-none">#{ref}</div>
                <div className="text-xs text-slate-600 mt-1">Quote #{(quoteId || '').toUpperCase()}</div>
              </div>
              <div className="shrink-0 flex flex-col items-center text-[11px] text-slate-500">
                <Camera className="w-6 h-6 text-[color:var(--mv-orange)]" />
                Screenshot me
              </div>
            </div>

            {/* Booking details */}
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div className="rounded-lg border border-slate-200 p-3 flex items-start gap-2">
                <CalIcon className="w-4 h-4 text-[color:var(--mv-orange)] shrink-0 mt-0.5" />
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">When</div>
                  <div className="font-bold text-[color:var(--mv-navy)]">{booking.date || '—'}</div>
                  <div className="text-slate-600 text-xs">{booking.time || '—'}</div>
                </div>
              </div>
              <div className="rounded-lg border border-slate-200 p-3 flex items-start gap-2">
                <Truck className="w-4 h-4 text-[color:var(--mv-orange)] shrink-0 mt-0.5" />
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Van & crew</div>
                  <div className="font-bold text-[color:var(--mv-navy)]">{quote.van_recommendation || '—'}</div>
                  <div className="text-slate-600 text-xs">{quote.hours_estimate || ''} · {quote.from_postcode} → {quote.to_postcode}</div>
                </div>
              </div>
            </div>

            {/* Price */}
            <div className="mt-4 rounded-lg bg-slate-50 border border-slate-200 p-4 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-600">Final total</span>
                <strong className="text-[color:var(--mv-navy)] text-lg">£{finalPrice}</strong>
              </div>
              <div className="flex justify-between text-xs mt-1 text-slate-500">
                <span>Deposit paid · £{deposit}</span>
                <span>Balance on moving day · <strong className="text-[color:var(--mv-navy)]">£{balance}</strong></span>
              </div>
              {Array.isArray(booking.extras) && booking.extras.length > 0 && (
                <div className="mt-2 pt-2 border-t border-slate-200 text-xs text-slate-600">
                  Extras: {booking.extras.map((e) => `${e.label} (+£${e.price})`).join(' · ')}
                </div>
              )}
              {booking.weekday_discount > 0 && (
                <div className="mt-1 text-xs text-emerald-700 font-semibold">🎉 Weekday saver: −£{booking.weekday_discount}</div>
              )}
            </div>

            {/* Same-day urgent CTA */}
            {sameDay && (
              <div className="mt-4 rounded-lg bg-red-50 border-2 border-red-200 p-4 text-sm text-red-900">
                <div className="font-bold">⚠ Same-day booking — WhatsApp us right now</div>
                <div className="text-xs mt-1">So we don't miss anything urgent, send us a quick WhatsApp confirming the job.</div>
                <a href={waUrl} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1.5 bg-[#25D366] hover:bg-[#1ebc5a] text-white text-sm font-bold px-4 py-2 rounded-md" data-testid="sameday-wa-btn">
                  <MessageCircle className="w-4 h-4" /> WhatsApp Move Van Ltd
                </a>
              </div>
            )}

            {/* General support */}
            <div className="mt-5 rounded-xl bg-[color:var(--mv-navy)] text-white p-4">
              <div className="text-[11px] uppercase tracking-wider font-black text-[color:var(--mv-orange-2)]">Any issues or questions?</div>
              <div className="text-sm mt-1 text-white/90">WhatsApp is the fastest way to reach us — our team is on 24/7.</div>
              <div className="mt-3 flex flex-col sm:flex-row gap-2">
                <a href={waUrl} target="_blank" rel="noreferrer" className="flex-1 bg-[#25D366] hover:bg-[#1ebc5a] text-white font-bold text-sm px-4 py-2.5 rounded-md inline-flex items-center justify-center gap-2" data-testid="support-wa-btn">
                  <MessageCircle className="w-4 h-4" /> WhatsApp us
                </a>
                <a href="tel:07985291097" className="flex-1 bg-white/10 hover:bg-white/15 text-white font-bold text-sm px-4 py-2.5 rounded-md inline-flex items-center justify-center gap-2 border border-white/20">
                  <Phone className="w-4 h-4" /> 07985 291097
                </a>
              </div>
            </div>

            <div className="mt-6 pt-4 border-t border-slate-100 text-center text-xs text-slate-500">
              <Link to="/" className="font-semibold hover:text-[color:var(--mv-orange)]">← Back to Move Van Ltd</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
